/**
 * PaymentDialog — B1.8 PaymentForm smart wrapper (Faz 1.3).
 *
 * Opens from CashierPage's "Pay" action. Collects one or more tenders
 * (cash + card + gift_card + transfer) and returns the assembled
 * `CashierTender[]` list through `onConfirm`. The Cashier feature then
 * drives `commitTender` to finalize the order.
 *
 * Boundary:
 *   - Owns: tender composition UX, card 3DS modal, cash keypad math
 *   - Does NOT own: order mutation, cashier cache invalidation, receipt display
 *
 * Backed by ADR-109 (Business Payment & Tender Engine).
 * UX invariants: tablet-first (≥56px primary actions, 48px keypad tiles,
 * 44px secondary), numeric typography (JetBrains Mono / font-mono),
 * biz-* tokens only, no hardcoded Turkish strings.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowClockwise,
  CheckCircle,
  CreditCard,
  Gift,
  Money,
  Receipt,
  ShieldCheck,
  SpinnerGap,
  Trash,
  XCircle,
} from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';

import { Button } from '../../shared/ui/Button';
import {
  computeCashTender,
  detectCardBrand,
  formatCardNumber,
  isExpiryValid,
  isValidLuhn,
  normalizeCardNumber,
  useConfirm3DS,
  useStartCardPayment,
  useStartGiftCardPayment,
  useStartTransferPayment,
  type CardFormDraft,
  type PaymentIntentSnapshot,
} from '../../features/payment';
import type {
  CashierPaymentMethod,
  CashierTender,
} from '../../features/cashier';

// ----------------------------------------------------------------------------
// Props
// ----------------------------------------------------------------------------

export interface PaymentDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly totalMinor: number;
  readonly currency: string;
  readonly orderId: string;
  readonly availableTenders: readonly CashierPaymentMethod[];
  readonly isCommitting: boolean;
  readonly onConfirm: (tenders: readonly CashierTender[]) => void;
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function formatCurrency(minor: number, currency = 'TRY'): string {
  const symbol = currency === 'TRY' ? '₺' : currency;
  const value = minor / 100;
  return `${symbol}${value.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const ACTIVE_METHODS: readonly CashierPaymentMethod[] = [
  'cash',
  'card',
  'gift_card',
  'transfer',
];

const METHOD_ICON: Record<
  CashierPaymentMethod,
  (typeof Money) | (typeof CreditCard) | (typeof Gift) | (typeof Receipt)
> = {
  cash: Money,
  card: CreditCard,
  gift_card: Gift,
  loyalty_points: ShieldCheck,
  transfer: Receipt,
  on_account: ShieldCheck,
};

const THREE_DS_TIMEOUT_SECONDS = 180;

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export function PaymentDialog({
  open,
  onOpenChange,
  totalMinor,
  currency,
  orderId,
  availableTenders,
  isCommitting,
  onConfirm,
}: PaymentDialogProps): JSX.Element {
  const { t } = useTranslation(['payment', 'common']);
  const [tenders, setTenders] = useState<CashierTender[]>([]);
  const [activeMethod, setActiveMethod] = useState<CashierPaymentMethod>('cash');

  const tenderedMinor = useMemo(
    () => tenders.reduce((acc, t) => acc + t.amountMinor, 0),
    [tenders],
  );
  const outstandingMinor = Math.max(0, totalMinor - tenderedMinor);
  const changeMinor = Math.max(0, tenderedMinor - totalMinor);
  const sufficient = tenderedMinor >= totalMinor;

  useEffect(() => {
    if (!open) {
      setTenders([]);
      setActiveMethod('cash');
    }
  }, [open]);

  const pushTender = useCallback((tender: CashierTender) => {
    setTenders((prev) => [...prev, tender]);
  }, []);

  const removeTender = useCallback((index: number) => {
    setTenders((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleConfirm = useCallback(() => {
    if (!sufficient) return;
    onConfirm(tenders);
  }, [onConfirm, sufficient, tenders]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-biz-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex h-[min(720px,90vh)] w-[min(960px,92vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-biz-card border border-biz-border bg-biz-surface shadow-biz-lg focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95">
          <DialogHeader
            total={totalMinor}
            outstanding={outstandingMinor}
            change={changeMinor}
            currency={currency}
          />
          <div className="flex flex-1 gap-biz-md overflow-hidden p-biz-md">
            <div className="flex w-[260px] shrink-0 flex-col gap-2">
              <p className="text-biz-xs font-semibold uppercase tracking-wide text-biz-muted-foreground">
                {t('payment:dialog.methodSectionTitle')}
              </p>
              {ACTIVE_METHODS.map((method) => {
                const Icon = METHOD_ICON[method];
                const unavailable = !availableTenders.includes(method);
                const active = !unavailable && method === activeMethod;
                return (
                  <button
                    key={method}
                    type="button"
                    disabled={unavailable}
                    onClick={() => setActiveMethod(method)}
                    className={[
                      'flex items-center gap-3 rounded-biz-btn border px-4 py-3 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
                      unavailable && 'cursor-not-allowed opacity-40',
                      active
                        ? 'border-biz-primary bg-biz-primary/10 text-biz-primary shadow-biz-sm'
                        : 'border-biz-border bg-biz-surface text-biz-foreground hover:bg-biz-muted',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <Icon size={22} weight={active ? 'fill' : 'regular'} />
                    <div className="flex-1">
                      <p className="text-biz-sm font-semibold">
                        {t(`payment:method.${method}`)}
                      </p>
                      {unavailable ? (
                        <p className="text-[10px] text-biz-muted-foreground">
                          {t('payment:method.unavailable')}
                        </p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
              <p className="mt-auto text-[11px] leading-snug text-biz-muted-foreground">
                {t('payment:dialog.splitHint')}
              </p>
            </div>
            <div className="flex flex-1 flex-col gap-biz-md overflow-hidden">
              <MethodPanel
                method={activeMethod}
                orderId={orderId}
                outstandingMinor={outstandingMinor}
                currency={currency}
                onTender={pushTender}
              />
              <TenderList
                tenders={tenders}
                onRemove={removeTender}
                currency={currency}
              />
            </div>
          </div>
          <DialogFooter
            sufficient={sufficient}
            tendersCount={tenders.length}
            onConfirm={handleConfirm}
            onClose={() => onOpenChange(false)}
            isCommitting={isCommitting}
            currency={currency}
            changeMinor={changeMinor}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ----------------------------------------------------------------------------
// Header
// ----------------------------------------------------------------------------

function DialogHeader({
  total,
  outstanding,
  change,
  currency,
}: {
  total: number;
  outstanding: number;
  change: number;
  currency: string;
}): JSX.Element {
  const { t } = useTranslation(['payment']);
  return (
    <header className="flex items-center justify-between border-b border-biz-border bg-biz-surface-elevated px-biz-lg py-biz-md">
      <div>
        <p className="text-biz-xs uppercase tracking-wide text-biz-muted-foreground">
          {t('payment:dialog.totalLabel')}
        </p>
        <p className="font-mono text-[28px] font-bold leading-tight text-biz-foreground">
          {formatCurrency(total, currency)}
        </p>
      </div>
      <div className="flex items-center gap-biz-xl">
        <div className="text-right">
          <p className="text-biz-xs uppercase tracking-wide text-biz-muted-foreground">
            {t('payment:dialog.outstandingLabel')}
          </p>
          <p
            className={
              'font-mono text-biz-xl font-semibold ' +
              (outstanding > 0 ? 'text-biz-warning' : 'text-biz-success')
            }
          >
            {formatCurrency(outstanding, currency)}
          </p>
        </div>
        {change > 0 ? (
          <div className="text-right">
            <p className="text-biz-xs uppercase tracking-wide text-biz-muted-foreground">
              {t('payment:dialog.changeLabel')}
            </p>
            <p className="font-mono text-biz-xl font-semibold text-biz-primary">
              {formatCurrency(change, currency)}
            </p>
          </div>
        ) : null}
        <Dialog.Close asChild>
          <button
            type="button"
            aria-label={t('payment:dialog.close')}
            className="flex h-11 w-11 items-center justify-center rounded-biz-btn text-biz-muted-foreground hover:bg-biz-muted hover:text-biz-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
          >
            <XCircle size={22} weight="regular" />
          </button>
        </Dialog.Close>
      </div>
    </header>
  );
}

// ----------------------------------------------------------------------------
// Footer
// ----------------------------------------------------------------------------

function DialogFooter({
  sufficient,
  tendersCount,
  onConfirm,
  onClose,
  isCommitting,
  currency,
  changeMinor,
}: {
  sufficient: boolean;
  tendersCount: number;
  onConfirm: () => void;
  onClose: () => void;
  isCommitting: boolean;
  currency: string;
  changeMinor: number;
}): JSX.Element {
  const { t } = useTranslation(['payment']);
  return (
    <footer className="flex items-center justify-between gap-biz-md border-t border-biz-border bg-biz-surface-elevated px-biz-lg py-biz-md">
      <Button intent="ghost" size="md" onClick={onClose} disabled={isCommitting}>
        {t('payment:dialog.cancel')}
      </Button>
      <div className="flex flex-col items-end">
        {changeMinor > 0 ? (
          <p className="mb-1 font-mono text-biz-xs text-biz-muted-foreground">
            {t('payment:dialog.changeDue', {
              amount: formatCurrency(changeMinor, currency),
            })}
          </p>
        ) : null}
        <Button
          intent="primary"
          size="lg"
          onClick={onConfirm}
          disabled={!sufficient || tendersCount === 0 || isCommitting}
        >
          {isCommitting ? (
            <SpinnerGap size={20} className="animate-spin" />
          ) : (
            <CheckCircle size={20} weight="fill" />
          )}
          {t('payment:dialog.confirm')}
        </Button>
      </div>
    </footer>
  );
}

// ----------------------------------------------------------------------------
// Method panel router
// ----------------------------------------------------------------------------

interface MethodPanelProps {
  method: CashierPaymentMethod;
  orderId: string;
  outstandingMinor: number;
  currency: string;
  onTender: (tender: CashierTender) => void;
}

function MethodPanel(props: MethodPanelProps): JSX.Element {
  switch (props.method) {
    case 'cash':
      return <CashPanel {...props} />;
    case 'card':
      return <CardPanel {...props} />;
    case 'gift_card':
      return <GiftCardPanel {...props} />;
    case 'transfer':
      return <TransferPanel {...props} />;
    default:
      return <UnavailablePanel method={props.method} />;
  }
}

function UnavailablePanel({ method }: { method: CashierPaymentMethod }): JSX.Element {
  const { t } = useTranslation(['payment']);
  return (
    <div className="flex flex-1 items-center justify-center text-biz-muted-foreground">
      {t('payment:method.unavailable')} · {t(`payment:method.${method}`)}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Cash panel
// ----------------------------------------------------------------------------

function CashPanel({
  outstandingMinor,
  currency,
  onTender,
}: MethodPanelProps): JSX.Element {
  const { t } = useTranslation(['payment']);
  const [tenderedMinor, setTenderedMinor] = useState(0);

  useEffect(() => {
    setTenderedMinor(0);
  }, [outstandingMinor]);

  const calc = useMemo(
    () => computeCashTender({ totalMinor: outstandingMinor, tenderedMinor }),
    [outstandingMinor, tenderedMinor],
  );

  const pushDigit = useCallback((digit: string) => {
    setTenderedMinor((prev) => {
      const next = Number(`${prev}${digit}`);
      if (!Number.isFinite(next) || next > 99_999_900) return prev;
      return next;
    });
  }, []);

  const popDigit = useCallback(() => {
    setTenderedMinor((prev) => Math.floor(prev / 10));
  }, []);

  const clearDigits = useCallback(() => setTenderedMinor(0), []);

  const presets = useMemo(
    () => [
      outstandingMinor,
      Math.ceil(outstandingMinor / 10_000) * 10_000,
      Math.ceil(outstandingMinor / 20_000) * 20_000,
      Math.ceil(outstandingMinor / 50_000) * 50_000,
    ],
    [outstandingMinor],
  );

  const submit = useCallback(() => {
    if (!calc.sufficient) return;
    onTender({
      method: 'cash',
      amountMinor: calc.tenderedMinor,
      reference: null,
    });
    setTenderedMinor(0);
  }, [calc, onTender]);

  return (
    <div className="flex flex-1 flex-col gap-biz-md rounded-biz-card border border-biz-border bg-biz-muted/30 p-biz-md">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-biz-xs uppercase tracking-wide text-biz-muted-foreground">
            {t('payment:cash.received')}
          </p>
          <p className="font-mono text-[32px] font-bold leading-none text-biz-foreground">
            {formatCurrency(tenderedMinor, currency)}
          </p>
        </div>
        <div className="text-right">
          <p
            className={
              'font-mono text-biz-sm font-medium ' +
              (calc.sufficient ? 'text-biz-success' : 'text-biz-warning')
            }
          >
            {calc.sufficient
              ? t('payment:cash.sufficient')
              : t('payment:cash.insufficient', {
                  amount: formatCurrency(
                    outstandingMinor - tenderedMinor,
                    currency,
                  ),
                })}
          </p>
          {calc.changeMinor > 0 ? (
            <p className="font-mono text-biz-base font-bold text-biz-primary">
              +{formatCurrency(calc.changeMinor, currency)}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-[11px] uppercase tracking-wide text-biz-muted-foreground">
          {t('payment:cash.quickAmounts')}
        </p>
        <div className="grid grid-cols-4 gap-2">
          {presets.map((p, i) => (
            <button
              key={`${p}-${i}`}
              type="button"
              onClick={() => setTenderedMinor(p)}
              className="h-11 rounded-biz-btn border border-biz-border bg-biz-surface font-mono text-biz-sm font-medium text-biz-foreground hover:bg-biz-primary/10 hover:text-biz-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
            >
              {formatCurrency(p, currency)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => pushDigit(d)}
            className="flex h-12 items-center justify-center rounded-biz-btn border border-biz-border bg-biz-surface font-mono text-biz-lg font-semibold text-biz-foreground hover:bg-biz-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          onClick={clearDigits}
          className="flex h-12 items-center justify-center rounded-biz-btn border border-biz-border bg-biz-surface font-mono text-biz-sm font-medium text-biz-muted-foreground hover:bg-biz-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
        >
          C
        </button>
        <button
          type="button"
          onClick={() => pushDigit('0')}
          className="flex h-12 items-center justify-center rounded-biz-btn border border-biz-border bg-biz-surface font-mono text-biz-lg font-semibold text-biz-foreground hover:bg-biz-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
        >
          0
        </button>
        <button
          type="button"
          onClick={popDigit}
          className="flex h-12 items-center justify-center rounded-biz-btn border border-biz-border bg-biz-surface text-biz-muted-foreground hover:bg-biz-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
          aria-label={t('payment:cash.clear')}
        >
          ←
        </button>
      </div>

      <Button
        intent="primary"
        size="lg"
        disabled={!calc.sufficient}
        onClick={submit}
      >
        <Money size={20} weight="fill" />
        {t('payment:dialog.submit')}
      </Button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Card panel
// ----------------------------------------------------------------------------

function CardPanel({
  orderId,
  outstandingMinor,
  currency,
  onTender,
}: MethodPanelProps): JSX.Element {
  const { t } = useTranslation(['payment']);
  const [draft, setDraft] = useState<CardFormDraft>({
    cardHolder: '',
    cardNumber: '',
    expMonth: 0,
    expYear: 0,
    cvv: '',
    saveCard: false,
  });
  const [intent, setIntent] = useState<PaymentIntentSnapshot | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const startPayment = useStartCardPayment();
  const confirm3ds = useConfirm3DS();

  const brand = useMemo(() => detectCardBrand(draft.cardNumber), [draft.cardNumber]);
  const normalized = useMemo(
    () => normalizeCardNumber(draft.cardNumber),
    [draft.cardNumber],
  );
  const formatted = useMemo(
    () => formatCardNumber(draft.cardNumber, brand),
    [draft.cardNumber, brand],
  );
  const expOk = isExpiryValid(draft.expMonth, draft.expYear);
  const luhnOk = isValidLuhn(normalized);
  const canSubmit =
    draft.cardHolder.trim().length >= 3 &&
    luhnOk &&
    expOk &&
    draft.cvv.length >= 3 &&
    !startPayment.isPending;

  const handleSubmit = useCallback(() => {
    if (!luhnOk) {
      setValidationError(t('payment:card.validationInvalid'));
      return;
    }
    if (!expOk) {
      setValidationError(t('payment:card.validationExpiry'));
      return;
    }
    if (!draft.cardHolder.trim() || draft.cvv.length < 3) {
      setValidationError(t('payment:card.validationMissing'));
      return;
    }
    setValidationError(null);
    startPayment.mutate(
      {
        orderId,
        amountMinor: outstandingMinor,
        currency,
        idempotencyKey: `${orderId}:card:${outstandingMinor}:${Date.now()}`,
        card: { ...draft, cardNumber: normalized },
      },
      {
        onSuccess: (snap) => setIntent(snap),
      },
    );
  }, [draft, luhnOk, expOk, normalized, orderId, outstandingMinor, currency, startPayment, t]);

  const handle3dsResult = useCallback(
    (result: 'success' | 'failure', conversationToken?: string | null) => {
      if (!intent) return;
      confirm3ds.mutate(
        {
          paymentId: intent.paymentId,
          // Live-mode bridge: BE expects payment_intents.id UUID +
          // Iyzico conversation token. In mock mode `paymentIntentId` is null
          // and `conversationToken` is undefined — the mock impl ignores both
          // and routes off `threeDsResult` instead.
          paymentIntentId: intent.paymentIntentId,
          conversationToken: conversationToken ?? null,
          threeDsResult: result,
        },
        { onSuccess: (snap) => setIntent(snap) },
      );
    },
    [intent, confirm3ds],
  );

  useEffect(() => {
    if (intent?.status === 'succeeded') {
      onTender({
        method: 'card',
        amountMinor: intent.amountMinor,
        reference: intent.reference,
        // ADR-109 — bridge the platform `payment_intents.id` UUID into
        // `biz_order_tenders.payment_intent_id` so the cashier row joins
        // back to the gateway round-trip for audit / reconciliation. NULL
        // in mock mode (no platform-side intent exists).
        paymentIntentId: intent.paymentIntentId,
      });
      setDraft({
        cardHolder: '',
        cardNumber: '',
        expMonth: 0,
        expYear: 0,
        cvv: '',
        saveCard: false,
      });
      setIntent(null);
    }
  }, [intent, onTender]);

  return (
    <div className="flex flex-1 flex-col gap-biz-md rounded-biz-card border border-biz-border bg-biz-muted/30 p-biz-md">
      <div className="grid grid-cols-1 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-biz-xs font-medium text-biz-muted-foreground">
            {t('payment:card.holder')}
          </span>
          <input
            type="text"
            autoComplete="cc-name"
            value={draft.cardHolder}
            onChange={(e) => setDraft({ ...draft, cardHolder: e.target.value })}
            placeholder={t('payment:card.holderPlaceholder')}
            className="h-11 rounded-biz-btn border border-biz-border bg-biz-surface px-3 text-biz-foreground placeholder:text-biz-muted-foreground focus:outline-none focus:ring-2 focus:ring-biz-ring"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="flex items-center justify-between text-biz-xs font-medium text-biz-muted-foreground">
            <span>{t('payment:card.number')}</span>
            {brand !== 'unknown' ? (
              <span className="uppercase tracking-wider text-biz-primary">
                {t('payment:card.brandDetected', { brand })}
              </span>
            ) : null}
          </span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-number"
            value={formatted}
            onChange={(e) =>
              setDraft({ ...draft, cardNumber: e.target.value })
            }
            placeholder={t('payment:card.numberPlaceholder')}
            className="h-12 rounded-biz-btn border border-biz-border bg-biz-surface px-3 font-mono text-biz-lg tracking-wider text-biz-foreground placeholder:text-biz-muted-foreground focus:outline-none focus:ring-2 focus:ring-biz-ring"
          />
        </label>
        <div className="grid grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-biz-xs font-medium text-biz-muted-foreground">
              {t('payment:card.expMonth')}
            </span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={2}
              autoComplete="cc-exp-month"
              value={draft.expMonth ? String(draft.expMonth).padStart(2, '0') : ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  expMonth: Number(e.target.value.replace(/\D/g, '')) || 0,
                })
              }
              placeholder="AA"
              className="h-11 rounded-biz-btn border border-biz-border bg-biz-surface px-3 text-center font-mono text-biz-base text-biz-foreground focus:outline-none focus:ring-2 focus:ring-biz-ring"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-biz-xs font-medium text-biz-muted-foreground">
              {t('payment:card.expYear')}
            </span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              autoComplete="cc-exp-year"
              value={draft.expYear ? String(draft.expYear) : ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  expYear: Number(e.target.value.replace(/\D/g, '')) || 0,
                })
              }
              placeholder="YYYY"
              className="h-11 rounded-biz-btn border border-biz-border bg-biz-surface px-3 text-center font-mono text-biz-base text-biz-foreground focus:outline-none focus:ring-2 focus:ring-biz-ring"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-biz-xs font-medium text-biz-muted-foreground">
              {t('payment:card.cvv')}
            </span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              autoComplete="cc-csc"
              value={draft.cvv}
              onChange={(e) =>
                setDraft({ ...draft, cvv: e.target.value.replace(/\D/g, '') })
              }
              placeholder={t('payment:card.cvvPlaceholder')}
              className="h-11 rounded-biz-btn border border-biz-border bg-biz-surface px-3 text-center font-mono text-biz-base text-biz-foreground focus:outline-none focus:ring-2 focus:ring-biz-ring"
            />
          </label>
        </div>
        <label className="flex items-start gap-2 text-biz-xs text-biz-muted-foreground">
          <input
            type="checkbox"
            checked={draft.saveCard}
            onChange={(e) => setDraft({ ...draft, saveCard: e.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-biz-border text-biz-primary focus:ring-biz-ring"
          />
          <span>
            <span className="font-medium text-biz-foreground">
              {t('payment:card.saveCard')}
            </span>
            <br />
            {t('payment:card.saveCardHint')}
          </span>
        </label>
      </div>
      {validationError ? (
        <p className="text-biz-xs text-biz-danger">{validationError}</p>
      ) : null}
      {intent?.status === 'failed' ? (
        <p className="rounded-biz-btn bg-biz-danger/10 px-3 py-2 text-biz-xs text-biz-danger">
          {intent.errorMessage ?? t('payment:card.resultFailed')}
        </p>
      ) : null}
      <div className="mt-auto flex items-center gap-2">
        <Button
          intent="primary"
          size="lg"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {startPayment.isPending ? (
            <SpinnerGap size={20} className="animate-spin" />
          ) : (
            <CreditCard size={20} weight="fill" />
          )}
          {t('payment:card.submit')}{' '}
          <span className="ml-2 font-mono">
            {formatCurrency(outstandingMinor, currency)}
          </span>
        </Button>
        {intent?.status === 'failed' ? (
          <Button
            intent="ghost"
            size="md"
            onClick={() => setIntent(null)}
          >
            <ArrowClockwise size={18} />
            {t('payment:card.retry')}
          </Button>
        ) : null}
      </div>

      <AnimatePresence>
        {intent?.status === 'pending_3ds' && intent.threeDsHtml ? (
          <ThreeDsOverlay
            html={intent.threeDsHtml}
            paymentId={intent.paymentId}
            paymentIntentId={intent.paymentIntentId}
            onResult={handle3dsResult}
            onCancel={() => setIntent(null)}
            isConfirming={confirm3ds.isPending}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

// ----------------------------------------------------------------------------
// 3DS overlay
// ----------------------------------------------------------------------------

function ThreeDsOverlay({
  html,
  paymentId,
  paymentIntentId,
  onResult,
  onCancel,
  isConfirming,
}: {
  html: string;
  paymentId: string;
  paymentIntentId: string | null;
  onResult: (r: 'success' | 'failure', conversationToken?: string | null) => void;
  onCancel: () => void;
  isConfirming: boolean;
}): JSX.Element {
  const { t } = useTranslation(['payment']);
  const [remaining, setRemaining] = useState(THREE_DS_TIMEOUT_SECONDS);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const id = window.setInterval(() => {
      setRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (remaining === 0) {
      onResult('failure');
    }
  }, [remaining, onResult]);

  useEffect(() => {
    function handler(ev: MessageEvent) {
      if (!ev.data || typeof ev.data !== 'object') return;
      const data = ev.data as {
        type?: string;
        paymentId?: string;
        paymentIntentId?: string;
        conversationToken?: string;
        status?: string;
      };
      if (data.type !== 'business.3ds.result') return;
      if (data.status !== 'success' && data.status !== 'failure') return;

      // Mock provider posts `{ paymentId, status }`; live Iyzico bridge
      // (callback HTML page hosted at `/business/payment/3ds-callback`)
      // posts `{ paymentIntentId, conversationToken, status }`. Either id
      // path is acceptable as long as it identifies the current intent.
      const matchesMock = data.paymentId === paymentId;
      const matchesLive =
        paymentIntentId !== null && data.paymentIntentId === paymentIntentId;
      if (!matchesMock && !matchesLive) return;

      onResult(data.status, data.conversationToken ?? null);
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [paymentId, paymentIntentId, onResult]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-10 flex flex-col bg-biz-background/95 backdrop-blur-sm"
    >
      <header className="flex items-center justify-between border-b border-biz-border bg-biz-surface px-biz-lg py-biz-md">
        <div className="flex items-center gap-2">
          <ShieldCheck size={22} weight="fill" className="text-biz-primary" />
          <div>
            <p className="text-biz-sm font-semibold text-biz-foreground">
              {t('payment:threeDs.title')}
            </p>
            <p className="font-mono text-biz-xs text-biz-muted-foreground">
              {t('payment:threeDs.timeoutWarning', { remaining })}
            </p>
          </div>
        </div>
        <Button
          intent="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isConfirming}
        >
          {t('payment:threeDs.cancel')}
        </Button>
      </header>
      <div className="flex-1 overflow-hidden bg-biz-background p-biz-md">
        <iframe
          ref={iframeRef}
          title="3D Secure"
          srcDoc={html}
          sandbox="allow-scripts"
          className="h-full w-full rounded-biz-card border border-biz-border bg-white"
        />
      </div>
      <footer className="flex items-center justify-between gap-2 border-t border-biz-border bg-biz-surface px-biz-lg py-biz-md">
        <p className="flex-1 text-biz-xs text-biz-muted-foreground">
          {t('payment:threeDs.description', { remaining })}
        </p>
        {isConfirming ? (
          <span className="flex items-center gap-2 text-biz-sm text-biz-primary">
            <SpinnerGap size={16} className="animate-spin" />
            {t('payment:status.submitting')}
          </span>
        ) : null}
      </footer>
    </motion.div>
  );
}

// ----------------------------------------------------------------------------
// Gift card panel
// ----------------------------------------------------------------------------

function GiftCardPanel({
  outstandingMinor,
  currency,
  onTender,
}: MethodPanelProps): JSX.Element {
  const { t } = useTranslation(['payment']);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const redeem = useStartGiftCardPayment();

  const submit = useCallback(() => {
    if (!/^GC-[A-Z0-9]{6,10}$/i.test(code.trim())) {
      setError(t('payment:giftCard.validationFormat'));
      return;
    }
    setError(null);
    redeem.mutate(
      { orderId: 'active', amountMinor: outstandingMinor, currency, code: code.trim() },
      {
        onSuccess: (snap) => {
          if (snap.status === 'succeeded') {
            onTender({
              method: 'gift_card',
              amountMinor: snap.amountMinor,
              reference: snap.reference,
            });
            setCode('');
          } else {
            setError(snap.errorMessage ?? t('payment:giftCard.resultFailed'));
          }
        },
      },
    );
  }, [code, outstandingMinor, currency, redeem, onTender, t]);

  return (
    <div className="flex flex-1 flex-col gap-biz-md rounded-biz-card border border-biz-border bg-biz-muted/30 p-biz-md">
      <label className="flex flex-col gap-1">
        <span className="text-biz-xs font-medium text-biz-muted-foreground">
          {t('payment:giftCard.label')}
        </span>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={t('payment:giftCard.placeholder')}
          className="h-12 rounded-biz-btn border border-biz-border bg-biz-surface px-3 font-mono text-biz-lg tracking-wider text-biz-foreground placeholder:text-biz-muted-foreground focus:outline-none focus:ring-2 focus:ring-biz-ring"
        />
      </label>
      {error ? (
        <p className="rounded-biz-btn bg-biz-danger/10 px-3 py-2 text-biz-xs text-biz-danger">
          {error}
        </p>
      ) : null}
      <Button
        intent="primary"
        size="lg"
        onClick={submit}
        disabled={redeem.isPending}
      >
        {redeem.isPending ? (
          <SpinnerGap size={20} className="animate-spin" />
        ) : (
          <Gift size={20} weight="fill" />
        )}
        {t('payment:giftCard.submit')}
      </Button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Transfer panel
// ----------------------------------------------------------------------------

function TransferPanel({
  outstandingMinor,
  currency,
  onTender,
}: MethodPanelProps): JSX.Element {
  const { t } = useTranslation(['payment']);
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const record = useStartTransferPayment();

  const submit = useCallback(() => {
    if (reference.trim().length < 4) {
      setError(t('payment:transfer.validationMissing'));
      return;
    }
    setError(null);
    record.mutate(
      {
        orderId: 'active',
        amountMinor: outstandingMinor,
        currency,
        reference: reference.trim(),
      },
      {
        onSuccess: (snap) => {
          if (snap.status === 'succeeded') {
            onTender({
              method: 'transfer',
              amountMinor: snap.amountMinor,
              reference: snap.reference,
            });
            setReference('');
          } else {
            setError(snap.errorMessage ?? t('payment:transfer.validationMissing'));
          }
        },
      },
    );
  }, [reference, outstandingMinor, currency, record, onTender, t]);

  return (
    <div className="flex flex-1 flex-col gap-biz-md rounded-biz-card border border-biz-border bg-biz-muted/30 p-biz-md">
      <label className="flex flex-col gap-1">
        <span className="text-biz-xs font-medium text-biz-muted-foreground">
          {t('payment:transfer.label')}
        </span>
        <input
          type="text"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder={t('payment:transfer.placeholder')}
          className="h-11 rounded-biz-btn border border-biz-border bg-biz-surface px-3 text-biz-foreground placeholder:text-biz-muted-foreground focus:outline-none focus:ring-2 focus:ring-biz-ring"
        />
      </label>
      {error ? (
        <p className="rounded-biz-btn bg-biz-danger/10 px-3 py-2 text-biz-xs text-biz-danger">
          {error}
        </p>
      ) : null}
      <Button
        intent="primary"
        size="lg"
        onClick={submit}
        disabled={record.isPending}
      >
        {record.isPending ? (
          <SpinnerGap size={20} className="animate-spin" />
        ) : (
          <Receipt size={20} weight="fill" />
        )}
        {t('payment:transfer.submit')}
      </Button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Tender list
// ----------------------------------------------------------------------------

function TenderList({
  tenders,
  onRemove,
  currency,
}: {
  tenders: readonly CashierTender[];
  onRemove: (index: number) => void;
  currency: string;
}): JSX.Element {
  const { t } = useTranslation(['payment']);
  if (tenders.length === 0) {
    return (
      <div className="rounded-biz-card border border-dashed border-biz-border bg-biz-surface/50 p-3 text-center text-biz-xs text-biz-muted-foreground">
        {t('payment:dialog.tenderListEmpty')}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-biz-muted-foreground">
        {t('payment:dialog.tenderListTitle')}
      </p>
      <ul className="flex flex-col gap-1">
        {tenders.map((tender, i) => {
          const Icon = METHOD_ICON[tender.method];
          return (
            <li
              key={`${tender.method}-${i}`}
              className="flex items-center gap-2 rounded-biz-btn border border-biz-border bg-biz-surface px-3 py-2"
            >
              <Icon size={18} weight="fill" className="text-biz-primary" />
              <span className="flex-1 text-biz-sm font-medium text-biz-foreground">
                {t(`payment:method.${tender.method}`)}
                {tender.reference ? (
                  <span className="ml-2 font-mono text-biz-xs text-biz-muted-foreground">
                    · {tender.reference}
                  </span>
                ) : null}
              </span>
              <span className="font-mono text-biz-sm font-semibold text-biz-foreground">
                {formatCurrency(tender.amountMinor, currency)}
              </span>
              <button
                type="button"
                onClick={() => onRemove(i)}
                aria-label={t('payment:dialog.removeTender')}
                className="flex h-8 w-8 items-center justify-center rounded-biz-btn text-biz-muted-foreground hover:bg-biz-muted hover:text-biz-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
              >
                <Trash size={14} weight="regular" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default PaymentDialog;
